/**
 * The `create_branch` agent-action view capability — the render half of the
 * branch-creation approval card. The agent proposes a branch when the
 * active branch is protected (main); approving creates it and switches the
 * tab onto it. Registered as a side effect on import.
 */
import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type CreateBranchProposal = Extract<InFlightAgentActionProposal, { kind: 'create_branch' }>

function isCreateBranch(p: InFlightAgentActionProposal): p is CreateBranchProposal {
  return p.kind === 'create_branch'
}

function ProposalDetail({ proposal }: { proposal: CreateBranchProposal }) {
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        <div className="break-words">
          <span className="text-muted-foreground">Branch:</span>{' '}
          <span className="font-mono">{proposal.branchName}</span>
          <span className="text-muted-foreground"> (from {proposal.sourceName ?? 'main'})</span>
        </div>
        {proposal.reason ? (
          <div className="break-words">
            <span className="text-muted-foreground">Why:</span> {proposal.reason}
          </div>
        ) : null}
        <div className="break-words text-muted-foreground">
          Approving creates the branch and switches you onto it; the agent's graph writes will
          target it.
        </div>
      </div>
    </div>
  )
}

function SettledRowDetail({ proposal }: { proposal: CreateBranchProposal }) {
  const terminal = proposal.terminalState
  if (!terminal) return null
  if (terminal.kind === 'applied') {
    const name = proposal.createdBranch?.name ?? proposal.branchName
    return (
      <p className="px-1 text-10 text-emerald-600 dark:text-emerald-400">
        Branch {name} created — you are now on it.
      </p>
    )
  }
  if (terminal.kind === 'apply_failed') {
    return (
      <p className="px-1 text-10 text-destructive">Branch creation failed — {terminal.error}</p>
    )
  }
  return (
    <p className="px-1 text-10 text-muted-foreground">
      Rejected{proposal.rejectionReason ? ` — ${proposal.rejectionReason}` : ''}.
    </p>
  )
}

export const createBranchViewCapability: AgentActionViewCapability = {
  kind: 'create_branch',
  badgeLabel() {
    return 'branch'
  },
  renderDetail(proposal) {
    if (!isCreateBranch(proposal)) return null
    return <ProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isCreateBranch(proposal)) return null
    return <SettledRowDetail proposal={proposal} />
  }
}

registerAgentActionView(createBranchViewCapability)
